/**
 * Presentación de los commits: tono, icono y fechas legibles.
 *
 * El backend clasifica cada commit y envía un tono semántico; aquí se traduce a
 * utilidades de Tailwind (no se pueden construir clases dinámicamente).
 */

import {
  CircleDot,
  FileText,
  FlaskConical,
  GitMerge,
  Package,
  Palette,
  Recycle,
  Server,
  Settings,
  Sparkles,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import type { CommitTipo, CommitTone } from '../updates.types';

const TONES: Record<CommitTone, { badge: string; dot: string }> = {
  brand: { badge: 'bg-brand-50 text-brand-700 ring-brand-200', dot: 'bg-brand-500' },
  emerald: { badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  amber: { badge: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  rose: { badge: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' },
  ink: { badge: 'bg-ink-100 text-ink-700 ring-ink-200', dot: 'bg-ink-400' },
};

const DEFAULT_TONE: CommitTone = 'ink';

/** Icono representativo de cada tipo de cambio. */
const ICONS: Record<CommitTipo, LucideIcon> = {
  feat: Sparkles,
  fix: Wrench,
  refactor: Recycle,
  perf: Zap,
  docs: FileText,
  style: Palette,
  test: FlaskConical,
  chore: Settings,
  build: Package,
  ci: Server,
  merge: GitMerge,
  other: CircleDot,
};

export function resolveTone(tone: string | null | undefined): CommitTone {
  if (tone && tone in TONES) return tone as CommitTone;

  return DEFAULT_TONE;
}

/** Clases del distintivo del tipo de cambio. */
export function toneBadgeClasses(tone: string | null | undefined): string {
  return TONES[resolveTone(tone)].badge;
}

/** Clases del punto del tipo de cambio. */
export function toneDotClasses(tone: string | null | undefined): string {
  return TONES[resolveTone(tone)].dot;
}

export function commitIcon(tipo: CommitTipo): LucideIcon {
  return ICONS[tipo] ?? ICONS.other;
}

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export interface EtiquetaDia {
  /** "Hoy", "Ayer" o la fecha larga en español. */
  titulo: string;
  /** Texto relativo corto ("hace 3 días"). */
  relativo: string;
}

/** Etiqueta legible del día de un grupo de commits (`YYYY-MM-DD`). */
export function formatCommitDay(dia: string): EtiquetaDia {
  const fecha = new Date(`${dia}T00:00:00`);
  if (Number.isNaN(fecha.getTime())) return { titulo: dia, relativo: '' };

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const dias = Math.round((hoy.getTime() - fecha.getTime()) / 86_400_000);

  if (dias === 0) return { titulo: 'Hoy', relativo: 'hoy' };
  if (dias === 1) return { titulo: 'Ayer', relativo: 'ayer' };

  const titulo = capitalizar(
    fecha.toLocaleDateString('es-PE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
  );

  return { titulo, relativo: dias > 1 ? `hace ${dias} días` : `en ${Math.abs(dias)} días` };
}

/** Hora local del commit (`hh:mm`). */
export function formatCommitTime(fecha: string): string {
  const valor = new Date(fecha);
  if (Number.isNaN(valor.getTime())) return '';

  return valor.toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
